import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { DM_TYPES } from "../lib/s1000dData";
import { buildDmcInventoryFromSbs, buildDmcInventoryCsv } from "../lib/sbsDmc";
import { safeFilename, triggerDownload } from "../lib/seedPack";
import SbsImporter from "./SbsImporter";
import HelpIconButton from "./HelpIconButton";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight, ChevronDown, Layers, FileDown } from "lucide-react";

const COL_QTY = "w-20 border border-gray-300 rounded-none px-2 py-1 font-mono text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7]";
const COL_NAME = "flex-1 min-w-[12rem] border border-gray-300 rounded-none px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7]";

function NodeRow({ depth, name, generalQty, repairableQty, onName, onGen, onRep, onAddChild, onRemove, addChildLabel, removable = true, testIdPrefix, expanded, onToggle, hasChildren }) {
  const indent = { paddingLeft: `${depth * 1.25}rem` };
  return (
    <div className="flex items-center gap-2 py-1.5" style={indent} data-testid={`${testIdPrefix}-row`}>
      <button
        type="button"
        onClick={onToggle}
        className={"w-6 h-6 inline-flex items-center justify-center text-gray-500 hover:text-black " + (hasChildren ? "" : "opacity-30 cursor-default")}
        data-testid={`${testIdPrefix}-toggle`}
        aria-label="toggle"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      <input
        value={name}
        onChange={(e) => onName(e.target.value)}
        placeholder="Name"
        className={COL_NAME}
        data-testid={`${testIdPrefix}-name`}
      />
      <input
        type="number" min="0" value={generalQty}
        onChange={(e) => onGen(e.target.value)}
        className={COL_QTY}
        data-testid={`${testIdPrefix}-general-qty`}
      />
      <input
        type="number" min="0" value={repairableQty}
        onChange={(e) => onRep(e.target.value)}
        className={COL_QTY}
        data-testid={`${testIdPrefix}-repairable-qty`}
      />
      <div className="flex items-center gap-1 ml-2">
        {onAddChild && (
          <button
            type="button"
            onClick={onAddChild}
            className="text-[10px] uppercase font-bold tracking-wider border border-gray-300 px-2 py-1 hover:bg-black hover:text-white hover:border-black"
            data-testid={`${testIdPrefix}-add-child`}
          >
            <Plus size={11} className="inline-block mr-1" /> {addChildLabel}
          </button>
        )}
        {removable && (
          <button
            type="button"
            onClick={onRemove}
            className="text-gray-500 hover:text-[#FF2400] p-1"
            data-testid={`${testIdPrefix}-remove`}
            title="Remove"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function SystemBreakdownTab() {
  const { t } = useTranslation();
  const {
    estimate, sbsCounts, formatHours,
    addSystem, removeSystem, updateSystem,
    addSubsystem, removeSubsystem, updateSubsystem,
    addSubsubsystem, removeSubsubsystem, updateSubsubsystem,
    updateSbsField, updateSbsHoursPerItem, applySbsToDmMatrix,
  } = useEstimate();
  const sbs = estimate.sbsPlan;
  const [expanded, setExpanded] = useState({}); // id -> bool

  const toggle = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));
  const isOpen = (id, defaultOpen = true) => expanded[id] !== undefined ? expanded[id] : defaultOpen;

  const onSync = (mode) => {
    if (sbsCounts.totalItems === 0) {
      toast.error(t("sbs.toast.noItems"));
      return;
    }
    applySbsToDmMatrix(mode);
    toast.success(t("sbs.toast.synced"), {
      description: t("sbs.toast.syncedDesc", {
        gen: t(`dmTypeByCode.${sbs.generalDmType}`, { defaultValue: sbs.generalDmType }),
        rep: t(`dmTypeByCode.${sbs.repairableDmType}`, { defaultValue: sbs.repairableDmType }),
        mode: mode === "merge" ? t("sbs.merge") : t("sbs.replace"),
      }),
    });
  };

  const totalGenHours =
    sbsCounts.simple.general * (Number(sbs.hoursPerItem?.simple) || 0) +
    sbsCounts.intermediate.general * (Number(sbs.hoursPerItem?.intermediate) || 0) +
    sbsCounts.complex.general * (Number(sbs.hoursPerItem?.complex) || 0);
  const totalRepHours =
    sbsCounts.simple.repairable * (Number(sbs.hoursPerItem?.simple) || 0) +
    sbsCounts.intermediate.repairable * (Number(sbs.hoursPerItem?.intermediate) || 0) +
    sbsCounts.complex.repairable * (Number(sbs.hoursPerItem?.complex) || 0);

  // DMC inventory derived from the SBS tree using the active SNS preset.
  const dmcRows = buildDmcInventoryFromSbs(estimate);

  const onDownloadDmc = () => {
    if (dmcRows.length === 0) {
      toast.error(t("sbs.toast.noItems"));
      return;
    }
    const csv = buildDmcInventoryCsv(dmcRows);
    const base = safeFilename(estimate.projectName || "estimate");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    triggerDownload(blob, `${base}_DMC_inventory.csv`);
    toast.success(t("sbs.toast.dmcDownloaded"), { description: `${dmcRows.length} rows` });
  };

  return (
    <section className="space-y-6" data-testid="sbs-tab">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{t("sbs.section")}</p>
        <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
          <Layers size={22} className="text-[#002FA7]" /> {t("sbs.title")}
          <HelpIconButton anchor="ch2" testId="help-icon-sbs" />
        </h2>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">{t("sbs.description")}</p>
      </div>

      {/* Configuration panel */}
      <div className="bg-white border border-gray-300 p-5 space-y-4" data-testid="sbs-config">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block mb-1">
              {t("sbs.generalDmType")}
            </label>
            <select
              value={sbs.generalDmType}
              onChange={(e) => updateSbsField("generalDmType", e.target.value)}
              className="w-full border border-gray-300 rounded-none px-2 py-1.5 font-mono text-sm bg-white"
              data-testid="sbs-general-dm-type"
            >
              {DM_TYPES.map((d) => <option key={d.code} value={d.code}>{d.code} · {d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block mb-1">
              {t("sbs.repairableDmType")}
            </label>
            <select
              value={sbs.repairableDmType}
              onChange={(e) => updateSbsField("repairableDmType", e.target.value)}
              className="w-full border border-gray-300 rounded-none px-2 py-1.5 font-mono text-sm bg-white"
              data-testid="sbs-repairable-dm-type"
            >
              {DM_TYPES.map((d) => <option key={d.code} value={d.code}>{d.code} · {d.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["simple", "intermediate", "complex"].map((lvl) => (
              <div key={lvl}>
                <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block mb-1">
                  {t(`levels.${lvl}`, { defaultValue: lvl })} h
                </label>
                <input
                  type="number" min="0" step="0.5"
                  value={sbs.hoursPerItem?.[lvl] ?? 0}
                  onChange={(e) => updateSbsHoursPerItem(lvl, e.target.value)}
                  className="w-full border border-gray-300 rounded-none px-2 py-1.5 font-mono text-sm text-right"
                  data-testid={`sbs-hours-${lvl}`}
                />
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-600">{t("sbs.mappingHint")}</p>
      </div>

      {/* Tree editor */}
      <div className="bg-white border border-gray-300 p-5" data-testid="sbs-tree">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700">{t("sbs.tree.title")}</h3>
          <button
            type="button"
            onClick={addSystem}
            className="bg-black text-white hover:bg-[#002FA7] rounded-none px-3 py-2 font-bold text-sm inline-flex items-center gap-2"
            data-testid="sbs-add-system-btn"
          >
            <Plus size={14} /> {t("sbs.addSystem")}
          </button>
        </div>

        {/* Column header */}
        <div className="flex items-center gap-2 py-1 px-2 bg-[#F3F4F6] border-b border-gray-300 text-[10px] font-bold uppercase tracking-wider text-gray-600">
          <span className="w-6" />
          <span className="flex-1 min-w-[12rem]">{t("sbs.col.name")}</span>
          <span className="w-20 text-right">{t("sbs.col.general")}</span>
          <span className="w-20 text-right">{t("sbs.col.repairable")}</span>
          <span className="ml-2 w-[10rem]" />
        </div>

        {(sbs.systems || []).length === 0 && (
          <div className="py-8 text-center text-sm text-gray-500" data-testid="sbs-empty">
            {t("sbs.empty")}
          </div>
        )}

        {(sbs.systems || []).map((sys) => {
          const sysOpen = isOpen(sys.id, true);
          return (
            <div key={sys.id} className="border-b border-gray-200 last:border-b-0" data-testid={`sbs-system-${sys.id}`}>
              <NodeRow
                depth={0}
                name={sys.name}
                generalQty={sys.generalQty}
                repairableQty={sys.repairableQty}
                onName={(v) => updateSystem(sys.id, "name", v)}
                onGen={(v) => updateSystem(sys.id, "generalQty", v)}
                onRep={(v) => updateSystem(sys.id, "repairableQty", v)}
                onAddChild={() => { addSubsystem(sys.id); setExpanded((m) => ({ ...m, [sys.id]: true })); }}
                onRemove={() => removeSystem(sys.id)}
                addChildLabel={t("sbs.addSubsystem")}
                testIdPrefix={`sbs-system-${sys.id}`}
                expanded={sysOpen}
                onToggle={() => toggle(sys.id)}
                hasChildren={(sys.subsystems || []).length > 0}
              />
              {sysOpen && (sys.subsystems || []).map((sub) => {
                const subOpen = isOpen(sub.id, true);
                return (
                  <div key={sub.id} data-testid={`sbs-subsystem-${sub.id}`}>
                    <NodeRow
                      depth={1}
                      name={sub.name}
                      generalQty={sub.generalQty}
                      repairableQty={sub.repairableQty}
                      onName={(v) => updateSubsystem(sys.id, sub.id, "name", v)}
                      onGen={(v) => updateSubsystem(sys.id, sub.id, "generalQty", v)}
                      onRep={(v) => updateSubsystem(sys.id, sub.id, "repairableQty", v)}
                      onAddChild={() => { addSubsubsystem(sys.id, sub.id); setExpanded((m) => ({ ...m, [sub.id]: true })); }}
                      onRemove={() => removeSubsystem(sys.id, sub.id)}
                      addChildLabel={t("sbs.addSubsubsystem")}
                      testIdPrefix={`sbs-subsystem-${sub.id}`}
                      expanded={subOpen}
                      onToggle={() => toggle(sub.id)}
                      hasChildren={(sub.subsubsystems || []).length > 0}
                    />
                    {subOpen && (sub.subsubsystems || []).map((ss) => (
                      <NodeRow
                        key={ss.id}
                        depth={2}
                        name={ss.name}
                        generalQty={ss.generalQty}
                        repairableQty={ss.repairableQty}
                        onName={(v) => updateSubsubsystem(sys.id, sub.id, ss.id, "name", v)}
                        onGen={(v) => updateSubsubsystem(sys.id, sub.id, ss.id, "generalQty", v)}
                        onRep={(v) => updateSubsubsystem(sys.id, sub.id, ss.id, "repairableQty", v)}
                        onRemove={() => removeSubsubsystem(sys.id, sub.id, ss.id)}
                        testIdPrefix={`sbs-subsubsystem-${ss.id}`}
                        expanded={false}
                        onToggle={() => { }}
                        hasChildren={false}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Summary + sync */}
      <div className="bg-white border border-[#002FA7] p-5" data-testid="sbs-summary">
        <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-3">{t("sbs.summary.title")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {[
            { key: "simple", label: t("levels.simple", { defaultValue: "Simple" }), subtitle: t("sbs.level.systemSubtitle") },
            { key: "intermediate", label: t("levels.intermediate", { defaultValue: "Intermediate" }), subtitle: t("sbs.level.subsystemSubtitle") },
            { key: "complex", label: t("levels.complex", { defaultValue: "Complex" }), subtitle: t("sbs.level.subsubsystemSubtitle") },
          ].map(({ key, label, subtitle }) => (
            <div key={key} className="border border-gray-300 p-3" data-testid={`sbs-summary-${key}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</p>
              <p className="text-xs text-gray-500 mb-2">{subtitle}</p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t("sbs.col.general")}</span>
                <span className="font-mono font-bold" data-testid={`sbs-summary-${key}-general`}>{sbsCounts[key].general}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t("sbs.col.repairable")}</span>
                <span className="font-mono font-bold" data-testid={`sbs-summary-${key}-repairable`}>{sbsCounts[key].repairable}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-sm">
          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{t("sbs.summary.totalItems")}</p>
            <p className="font-mono text-xl font-bold mt-1" data-testid="sbs-total-items">{sbsCounts.totalItems}</p>
          </div>
          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {t("sbs.summary.generalHours")} · {sbs.generalDmType}
            </p>
            <p className="font-mono text-xl font-bold mt-1" data-testid="sbs-general-hours">{formatHours(totalGenHours)}</p>
          </div>
          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {t("sbs.summary.repairableHours")} · {sbs.repairableDmType}
            </p>
            <p className="font-mono text-xl font-bold mt-1" data-testid="sbs-repairable-hours">{formatHours(totalRepHours)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onSync("replace")}
            className="bg-[#002FA7] text-white hover:bg-black rounded-none px-4 py-2 font-bold text-sm inline-flex items-center gap-2"
            data-testid="sbs-sync-replace-btn"
          >
            {t("sbs.action.syncReplace")}
          </button>
          <button
            type="button"
            onClick={() => onSync("merge")}
            className="bg-white text-black border border-gray-300 hover:bg-gray-100 rounded-none px-4 py-2 font-bold text-sm inline-flex items-center gap-2"
            data-testid="sbs-sync-merge-btn"
          >
            {t("sbs.action.syncMerge")}
          </button>
          <button
            type="button"
            onClick={onDownloadDmc}
            className="bg-white text-black border border-gray-300 hover:bg-gray-100 rounded-none px-4 py-2 font-bold text-sm inline-flex items-center gap-2"
            data-testid="sbs-dmc-csv-btn"
          >
            <FileDown size={14} /> {t("sbs.action.dmcCsv")}
          </button>
          <p className="text-xs text-gray-600 ml-2 max-w-2xl">{t("sbs.action.hint")}</p>
        </div>
      </div>

      {/* DMC Inventory preview */}
      <div className="bg-white border border-gray-300 p-5" data-testid="sbs-dmc-preview">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700">{t("sbs.dmc.title")}</h3>
            <p className="text-xs text-gray-500 mt-0.5 max-w-3xl">{t("sbs.dmc.description")}</p>
          </div>
          <span className="font-mono text-xs text-gray-600" data-testid="sbs-dmc-count">
            {t("sbs.dmc.totalRows")}: <span className="font-bold text-black">{dmcRows.length}</span>
          </span>
        </div>
        {dmcRows.length === 0 ? (
          <p className="text-sm text-gray-500 py-4" data-testid="sbs-dmc-empty">{t("sbs.dmc.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono border-collapse" data-testid="sbs-dmc-table">
              <thead>
                <tr className="bg-[#F3F4F6] border-b border-gray-300">
                  <th className="px-2 py-1.5 text-left">#</th>
                  <th className="px-2 py-1.5 text-left">DMC</th>
                  <th className="px-2 py-1.5 text-left">{t("sbs.dmc.category")}</th>
                  <th className="px-2 py-1.5 text-left">{t("sbs.dmc.complexity")}</th>
                  <th className="px-2 py-1.5 text-left">{t("sbs.dmc.path")}</th>
                </tr>
              </thead>
              <tbody>
                {dmcRows.slice(0, 12).map((r, i) => (
                  <tr key={i} className="border-b border-gray-200" data-testid={`sbs-dmc-row-${i}`}>
                    <td className="px-2 py-1 text-gray-500">{i + 1}</td>
                    <td className="px-2 py-1 text-black">{r.dmc}</td>
                    <td className="px-2 py-1">
                      <span className={r.category === "general" ? "text-[#002FA7]" : "text-[#FF6B00]"}>{r.category}</span>
                    </td>
                    <td className="px-2 py-1 text-gray-600">{r.level}</td>
                    <td className="px-2 py-1 text-gray-600">
                      {r.systemName}{r.subsystemName ? ` › ${r.subsystemName}` : ""}{r.subsubsystemName ? ` › ${r.subsubsystemName}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {dmcRows.length > 12 && (
              <p className="text-[11px] text-gray-500 mt-2 font-mono" data-testid="sbs-dmc-truncated">
                {t("sbs.dmc.preview", { shown: 12, total: dmcRows.length })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Spreadsheet import */}
      <SbsImporter />
    </section>
  );
}
